import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, LogIn, UserRound, MessageCircleQuestion, MessagesSquare, Star, ChevronRight, Home, Info, CalendarDays, Ticket, Bed, Mic2, Handshake, Dumbbell, Clapperboard, Images, Shield, MoreHorizontal } from 'lucide-react';
import { useSignup } from '../context/SignupContext';
import Logo from './Logo';
import { mockData } from '../mock';
import { useAuth } from '../context/AuthContext';
import { pictureUrl } from '../lib/api';
import { useT } from '../context/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeToggle } from './ThemeToggle';
import { useContent } from '../context/ContentContext';
import { SiteFieldEditor } from './admin/SiteFieldEditor';

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [mobileExtrasOpen, setMobileExtrasOpen] = useState(false);
  const { user, logout } = useAuth();
  const { content } = useContent();
  const site = content.site || {};
  const { t } = useT();
  const { openSignup } = useSignup();
  const navigate = useNavigate();
  const icons = { '#top': Home, '#om': Info, '#program': CalendarDays, '#pamelding': Ticket, '#steder': Bed, '#talere': Mic2, '#partnere': Handshake, '/aktiviteter': Dumbbell, '/videoar': Clapperboard, '/galleri': Images };
  const menuRef = useRef(null);
  const mobileExtrasRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
      if (mobileExtrasRef.current && !mobileExtrasRef.current.contains(e.target)) setMobileExtrasOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleLogout = async () => {
    await logout();
    setShowMenu(false);
    navigate('/');
  };

  const avatar = pictureUrl(user?.picture);
  const linkHref = (href) => (href.startsWith('#') ? `/${href}` : href);

  return (
    <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-sm border-b border-white/5">
      <div className="max-w-[1600px] mx-auto px-3 md:px-8 h-16 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 md:gap-4 min-w-0 overflow-hidden">
          <button onClick={() => setOpen(true)} data-testid="nav-menu-button" aria-label="Opne meny" className="inline-flex items-center gap-2 h-10 pl-3 pr-3 md:pr-4 rounded-full border border-white/15 text-white hover:border-[#ff2e2e] hover:text-[#ff2e2e] transition-colors font-semibold text-sm">
            <Menu size={20} />
            <span className="hidden md:inline">{t('Meny')}</span>
          </button>
          <Link to="/" data-edit-fields="logoImage" className="shrink-0 flex items-center" data-testid="nav-home-logo" aria-label="Refuel Weekend – framside"><Logo size="sm" variant="wide" /></Link>
        </div>

        <nav className="flex items-center gap-1 md:gap-2 relative shrink-0" ref={menuRef} aria-label="Hurtigval">
          <Link to="/anmeldelser" data-testid="nav-review-button" title="Anmeld nettsida" className="hidden sm:inline-flex items-center gap-2 h-10 px-3 md:px-4 rounded-full text-white/75 hover:text-[#ffb400] hover:bg-white/5 font-semibold text-sm transition-colors">
            <Star size={17} />
            <span className="hidden lg:inline">{t('Anmeld nettsida')}</span>
          </Link>
          <Link to="/sporsmal" data-testid="nav-question-button" title="Spørsmål?" className="hidden sm:inline-flex items-center gap-2 h-10 px-3 md:px-4 rounded-full text-white/75 hover:text-white hover:bg-white/5 font-semibold text-sm transition-colors">
            <MessageCircleQuestion size={17} />
            <span className="hidden lg:inline">{t('Spørsmål?')}</span>
          </Link>
          <div className="hidden sm:block"><ThemeToggle /></div>
          <LanguageSwitcher />
          <span className="hidden sm:block w-px h-6 bg-white/10 mx-1" aria-hidden="true" />
          {user ? (
            <div className="relative">
              <button onClick={() => setShowMenu(!showMenu)} data-testid="nav-user-menu-button" className="flex items-center gap-2 h-10 pl-1 pr-1 md:pr-4 rounded-full border border-white/15 hover:border-[#ff2e2e] transition-colors">
                {avatar ? (
                  <img src={avatar} alt={user.name} className="w-8 h-8 rounded-full object-cover" data-testid="nav-avatar-image" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#ff2e2e] text-black font-bold flex items-center justify-center text-sm">{user.name?.[0]?.toUpperCase() || 'U'}</div>
                )}
                <span className="text-white text-sm font-semibold hidden md:block max-w-[140px] truncate" data-testid="nav-user-name">{user.name}</span>
              </button>
              {showMenu && (
                <div className="absolute right-0 top-full mt-2 min-w-[220px] rounded-2xl bg-[#0f0a17] border border-white/10 shadow-2xl overflow-hidden" data-testid="nav-user-menu">
                  <div className="px-4 py-3 border-b border-white/5">
                    <p className="text-white text-sm font-semibold">{user.name}</p>
                    <p className="text-white/50 text-xs truncate">{user.email}</p>
                  </div>
                  <Link to="/medlem" onClick={() => setShowMenu(false)} data-testid="nav-member-link" className="w-full flex items-center gap-2 px-4 py-3 text-white/80 hover:bg-white/5 hover:text-[#ff2e2e] transition-colors text-sm text-left">
                    <UserRound size={16} /> {t('Medlemsside')}
                  </Link>
                  <Link to="/medlemssenter" onClick={() => setShowMenu(false)} data-testid="nav-member-center-link" className="w-full flex items-center gap-2 px-4 py-3 text-white/80 hover:bg-white/5 hover:text-[#ff2e2e] transition-colors text-sm text-left">
                    <MessagesSquare size={16} /> Medlemssenter
                  </Link>
                  <button onClick={handleLogout} data-testid="nav-logout-button" className="w-full flex items-center gap-2 px-4 py-3 text-white/80 hover:bg-white/5 hover:text-[#ff2e2e] transition-colors text-sm text-left">
                    <LogOut size={16} /> {t('Logg ut')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={() => navigate('/login')} data-testid="nav-login-button" className="inline-flex items-center gap-2 h-10 px-4 md:px-5 rounded-full bg-[#ff2e2e] text-black font-bold text-sm whitespace-nowrap hover:bg-[#ff4d4d] transition-colors shadow-lg shadow-[#ff2e2e]/25">
              <LogIn size={16} />
              <span>{t('Logg inn')}</span>
            </button>
          )}
          <div className="relative sm:hidden" ref={mobileExtrasRef}>
            <button
              type="button"
              onClick={() => setMobileExtrasOpen((current) => !current)}
              aria-label="Flere valg"
              aria-expanded={mobileExtrasOpen}
              data-testid="nav-mobile-extras-button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white hover:border-[#ff2e2e] hover:text-[#ff2e2e]"
            >
              <MoreHorizontal size={20} />
            </button>
            {mobileExtrasOpen && (
              <div className="absolute right-0 top-full z-[70] mt-2 w-64 rounded-2xl border border-white/10 bg-[#0f0a17] p-3 shadow-2xl" data-testid="nav-mobile-extras-menu">
                <ThemeToggle showLabel />
                <Link to="/anmeldelser" onClick={() => setMobileExtrasOpen(false)} data-testid="nav-mobile-review-link" className="mt-2 flex items-center gap-3 rounded-xl px-3 py-2.5 text-white/80 hover:bg-white/5">
                  <Star size={17} className="text-[#ffb400]" /><span>{t('Anmeld nettsida')}</span>
                </Link>
                <Link to="/sporsmal" onClick={() => setMobileExtrasOpen(false)} data-testid="nav-mobile-question-link" className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-white/80 hover:bg-white/5">
                  <MessageCircleQuestion size={17} /><span>{t('Spørsmål?')}</span>
                </Link>
              </div>
            )}
          </div>
        </nav>
      </div>

      {open && createPortal(
        <div className="fixed inset-0 z-[60]" data-testid="nav-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 h-full max-h-[100dvh] w-[85vw] max-w-sm bg-[#0f0a17] border-r border-white/10 shadow-2xl flex flex-col animate-in slide-in-from-left duration-200" data-testid="nav-drawer-panel">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <Logo size="sm" variant="wide" />
              <button onClick={() => setOpen(false)} data-testid="nav-drawer-close" aria-label="Lukk meny" className="w-10 h-10 rounded-full border border-white/15 text-white flex items-center justify-center hover:border-[#ff2e2e] hover:text-[#ff2e2e]">
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 flex flex-col gap-1" data-testid="nav-drawer-nav">
              <div className="flex items-center justify-between gap-2 px-3 pt-1 pb-2">
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/35">{t('Utforsk')}</p>
                <SiteFieldEditor title="Rediger meny" fieldKeys={['navLinks', 'eventName', 'eventDate', 'venue']} testId="edit-site-nav-button" />
              </div>
              {(Array.isArray(site.navLinks) ? site.navLinks : mockData.navLinks).map((link, i) => {
                const Icon = icons[link.href] || ChevronRight;
                return (
                  <a
                    key={link.label}
                    href={linkHref(link.href)}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    style={{ animationDelay: `${i * 30}ms` }}
                    className="group flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/80 font-semibold hover:bg-white/5 hover:text-white transition-colors animate-[fadeUp_0.3s_ease-out_both]"
                  >
                    <span className="w-9 h-9 rounded-xl bg-[#ff2e2e]/10 text-[#ff2e2e] flex items-center justify-center group-hover:bg-[#ff2e2e] group-hover:text-black transition-colors"><Icon size={17} /></span>
                    <span className="flex-1">{t(link.label)}</span>
                    <ChevronRight size={16} className="text-white/25 group-hover:text-[#ff2e2e] group-hover:translate-x-0.5 transition-[color,transform]" />
                  </a>
                );
              })}
              <p className="px-3 pt-4 pb-2 text-[10px] font-bold uppercase tracking-[0.25em] text-white/35">{t('Meir')}</p>
              <Link to="/anmeldelser" onClick={() => setOpen(false)} data-testid="nav-drawer-review-link" className="group flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/80 font-semibold hover:bg-white/5 hover:text-white transition-colors">
                <span className="w-9 h-9 rounded-xl bg-[#ffb400]/10 text-[#ffb400] flex items-center justify-center group-hover:bg-[#ffb400] group-hover:text-black transition-colors"><Star size={17} /></span>
                <span className="flex-1">{t('Anmeld nettsida')}</span><ChevronRight size={16} className="text-white/25" />
              </Link>
              <Link to="/sporsmal" onClick={() => setOpen(false)} className="group flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/80 font-semibold hover:bg-white/5 hover:text-white transition-colors">
                <span className="w-9 h-9 rounded-xl bg-white/5 text-white/70 flex items-center justify-center group-hover:bg-white group-hover:text-black transition-colors"><MessageCircleQuestion size={17} /></span>
                <span className="flex-1">{t('Spørsmål?')}</span><ChevronRight size={16} className="text-white/25" />
              </Link>
              {user?.is_admin && (
                <Link to="/admin" onClick={() => setOpen(false)} data-testid="nav-drawer-admin-link" className="group flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/80 font-semibold hover:bg-white/5 hover:text-white transition-colors">
                  <span className="w-9 h-9 rounded-xl bg-[#ffb400]/10 text-[#ffb400] flex items-center justify-center"><Shield size={17} /></span>
                  <span className="flex-1">Adminpanel</span><ChevronRight size={16} className="text-white/25" />
                </Link>
              )}
            </nav>
            <div className="px-4 pb-4 pt-3 border-t border-white/10 space-y-3">
              <button type="button" onClick={() => { setOpen(false); openSignup(); }} data-testid="nav-drawer-signup-button" className="w-full h-12 rounded-full bg-[#ff2e2e] text-black font-bold uppercase tracking-wide hover:bg-[#ff4d4d] transition-colors shadow-lg shadow-[#ff2e2e]/25">
                {t('Meld deg på')}
              </button>
              <p className="text-white/35 text-[11px] text-center">{site.eventName || 'Refuel Weekend 27'} · {site.eventDate || '2.–4. april'} · {site.venue || 'Hjeltefjorden Arena'}</p>
            </div>
          </aside>
        </div>,
        document.body
      )}
    </header>
  );
};

export default Navbar;
